interface NormalizeNameOptions {
  removeAccents: boolean;
  allowHyphen?: boolean;
}

/**
 * Normalizes human-name input with optional accent stripping and hyphen support.
 * @param {unknown} input - Raw name input from the form.
 * @param {object} options - Normalization options for the current field.
 * @param {boolean} options.removeAccents - Removes orthographic accents when true.
 * @param {boolean} [options.allowHyphen=false] - Preserves hyphens when true.
 * @returns {string} Sanitized name value.
 */
function normalizeNameWithOptions(
  input: unknown,
  { removeAccents, allowHyphen = false }: NormalizeNameOptions,
): string {
  if (typeof input !== 'string') {
    return '';
  }

  /**
   * Strips leading whitespace and collapses consecutive spaces to one.
   * @param {string} str - Name string to normalize.
   * @returns {string} String with leading whitespace removed and consecutive spaces collapsed.
   */
  function normalizeSpaces(str: string): string {
    return str.trimStart().replace(/\s{2,}/g, ' ');
  }

  if (removeAccents) {
    const asciiPattern = allowHyphen ? /[^a-zA-Z\s-]+/g : /[^a-zA-Z\s]+/g;
    return normalizeSpaces(
      input
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(asciiPattern, ''),
    );
  }

  const accentPattern = allowHyphen
    ? /[^a-zA-Z\s´ÁÉÍÓÚáéíóúñÑ˜-]+/g
    : /[^a-zA-Z\s´ÁÉÍÓÚáéíóúñÑ˜]+/g;
  return normalizeSpaces(
    input
      .replace(accentPattern, '')
      // ´ and ˜ are allowed above so they reach this step as a pair with the
      // letter that follows. Removing both together guards against dead-key
      // composition errors: if the user presses a dead-key accent and then a
      // letter that does not combine with it (e.g. "concentraci´n" missing the
      // "o"), stripping only the accent would silently corrupt the name with an
      // unintended letter. Stripping the pair lets the user notice and retype.
      .replace(/([´˜].{1,1})+/g, ''),
  );
}

export { normalizeNameWithOptions };
